export const MAX_IMAGE_UPLOAD_DIMENSION = 2048;

const RESIZABLE_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png'];
const RESIZABLE_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png'];

function getFileExtension(fileName = '') {
    const parts = fileName.toLowerCase().split('.');
    return parts.length > 1 ? parts.pop() : '';
}

function resolveOutputMimeType(file) {
    const mimeType = (file.type || '').toLowerCase();
    if (mimeType === 'image/png' || getFileExtension(file.name) === 'png') {
        return 'image/png';
    }

    return 'image/jpeg';
}

function loadImageFromFile(file) {
    return new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(file);
        const image = new Image();

        image.onload = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(image);
        };

        image.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error('Failed to load image for upload preparation'));
        };

        image.src = objectUrl;
    });
}

function canvasToBlob(canvas, mimeType, quality) {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (blob) {
                    resolve(blob);
                    return;
                }

                reject(new Error('Failed to create upload image blob'));
            },
            mimeType,
            quality
        );
    });
}

function resolveScaledDimensions(width, height, maxDimension) {
    const largestDimension = Math.max(width, height);
    if (largestDimension <= maxDimension) {
        return {
            width,
            height,
        };
    }

    const scale = maxDimension / largestDimension;

    return {
        width: Math.max(1, Math.round(width * scale)),
        height: Math.max(1, Math.round(height * scale)),
    };
}

export async function downscaleImageForUpload(file, options = {}) {
    if (!file) {
        throw new Error('No file provided for image upload preparation');
    }

    let mimeType = (file.type || '').toLowerCase();
    const extension = getFileExtension(file.name);
    const isResizable = RESIZABLE_IMAGE_MIME_TYPES.includes(mimeType) || RESIZABLE_IMAGE_EXTENSIONS.includes(extension);

    // Only downscale formats we can re-encode without losing features (e.g. GIF animation).
    if (!isResizable) {
        return file;
    }

    const image = await loadImageFromFile(file);
    const maxDimension = options.maxDimension || MAX_IMAGE_UPLOAD_DIMENSION;
    const originalWidth = image.naturalWidth || image.width;
    const originalHeight = image.naturalHeight || image.height;
    const scaledDimensions = resolveScaledDimensions(originalWidth, originalHeight, maxDimension);

    if (scaledDimensions.width === originalWidth && scaledDimensions.height === originalHeight) {
        return file;
    }

    const canvas = document.createElement('canvas');
    canvas.width = scaledDimensions.width;
    canvas.height = scaledDimensions.height;

    const context = canvas.getContext('2d');
    if (!context) {
        throw new Error('Canvas is not available for image upload preparation');
    }

    // Keep uploads visually sharp while clamping extreme dimensions before sending them to the API.
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(image, 0, 0, scaledDimensions.width, scaledDimensions.height);

    mimeType = resolveOutputMimeType(file);
    const blob = await canvasToBlob(canvas, mimeType, mimeType === 'image/jpeg' ? 0.9 : undefined);
    const normalizedFile = new File([blob], file.name, {
        type: blob.type || mimeType,
        lastModified: file.lastModified || Date.now(),
    });

    return normalizedFile;
}

export async function createImageUploadFormData(file, options = {}) {
    const normalizedFile = await downscaleImageForUpload(file, options);
    const data = new FormData();
    const fallbackFileName = normalizedFile.name || (file && file.name) || 'image';

    data.append('file', normalizedFile, fallbackFileName);

    return data;
}
